import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import { LoadableDataState } from "@my-dld/utils/UIUtils";
import React, { Component } from "react";
import { TenantContractContext, updateTenantContractContext } from "@eres/ejari-core/contexts/TenantContractContext";
import { WebFormFieldProps } from "@eres/forms-web";
import StateHolder from "@eres/forms/StateHolder";
import { defaultErrorManager } from "@eres/forms/errormanager";
import FormSection from "@my-dld/components/Form/FormSection";
import Strings from "@my-dld/assets/strings";
import FormRow from "@eres/forms-web/FormRow";
import TextInputField from "@eres/forms-web/TextInputField";
import _ from "lodash";
import { dualDataManager } from "@eres/forms/valuemanager/DualDataManager";
import { PersonTenantX } from "../Contract/TenantForms/PersonTenant";
import DateInputField from "@eres/forms-web/DateInputField";
import { serverDateConverter } from "@my-dld/utils/LookupUtil";
import FormAction from "@my-dld/components/Form/FormAction";
import { navigate } from "@my-dld/utils/StoreUtils";
import { ErrorContainer } from "@eres/forms";
import { checkNonEmpty } from "../../utils";
import { isEmailAddress } from "@eres/core/dist/StringUtils";
import FormMandatory from "../FormMandatory";

interface Props extends RoutedViewProps {

}

interface State extends LoadableDataState {
	newData?: PersonTenantX;
	orgData?: PersonTenantX;
	isGCC?: boolean;
}

class TenantInfo extends Component<Props, State> {
	static contextType = TenantContractContext;
	context!: React.ContextType<typeof TenantContractContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			errorContainer: {},
			loadState: "loading",
		};
	}

	componentDidMount(): void {
		this.updateFromContext();
	}

	render() {
		const filedProps: Partial<WebFormFieldProps<any, any>> = {
			holder: StateHolder.buildStateHolder( this ),
			containerClassName: "col-md-6 form-group",
			valueManager: dualDataManager,
			errorManager: defaultErrorManager,
		};
		const { isGCC } = this.state;
		const data = this.getUpdatedData();
		return <div className="tender-registration-form">
			<FormSection title={ Strings.tenant_information }>
				<FormMandatory/>
				<FormRow { ...filedProps }>
					<TextInputField
						required={ true }
						id={ "Email" }
						label={ Strings.email }/>
				</FormRow>
			</FormSection>
			<FormSection title={ Strings.passport_information }>
				<FormMandatory/>
				<FormRow { ...filedProps }>
					<TextInputField
						required={ true }
						id={ "PassportNo" }
						label={ Strings.passport_number }/>
					<DateInputField
						required={ true }
						label={ Strings.passport_issue_date }
						id={ "PassportIssueDate" }
						extraOpts={ {
							maxDate: data.PassportExpiryDate?.asDate(),
						} }
						valueConverter={ serverDateConverter }/>
					<DateInputField
						required={ true }
						label={ Strings.passport_exp_date }
						id={ "PassportExpiryDate" }
						extraOpts={ {
							minDate: data.PassportIssueDate?.asDate(),
						} }
						valueConverter={ serverDateConverter }/>
				</FormRow>
			</FormSection>
			<FormSection title={ Strings.residence_information }>
				<FormMandatory/>
				<FormRow { ...filedProps }>
					<TextInputField
						required={ !isGCC }
						id={ "ResidenceVisaNumber" }
						label={ Strings.residence_visa_number }/>
					<DateInputField
						required={ !isGCC }
						id={ "VisaStartDate" }
						label={ Strings.visa_start_date }
						extraOpts={ {
							maxDate: data.VisaExpiryDate?.asDate(),
						} }
						valueConverter={ serverDateConverter }/>
					<DateInputField
						required={ !isGCC }
						id={ "VisaExpiryDate" }
						label={ Strings.visa_expiry_date }
						extraOpts={ {
							minDate: data.VisaStartDate?.asDate(),
						} }
						valueConverter={ serverDateConverter }/>
				</FormRow>
			</FormSection>
			<FormRow>
				<FormAction
					primaryActionLabel={ Strings.continue }
					onPrimaryAction={ this.onContinue }
					secondaryActionLabel={ Strings.back }
					onSecondaryAction={ this.onBack }/>
			</FormRow>
		</div>;
	}

	private updateFromContext() {
		const { contract } = this.context;
		if ( contract && contract.Tenants && contract.Tenants.length > 0 ) {
			const tenant = contract.Tenants[ 0 ];
			const personTenant = tenant.Person;
			const personTenantX: PersonTenantX = { Email: tenant.Email, ...personTenant };
			const state = {
				orgData: personTenantX || {},
				newData: personTenantX || {},
				isGCC: personTenant?.IsGCCNational === true,
			};
			this.setState( state );
		}
	}

	private getUpdatedData = ( state: State = this.state ) => {
		const data: PersonTenantX = {};
		_.merge( data, state.orgData, state.newData );
		return data;
	};

	private isValid() {
		const data = this.getUpdatedData();
		const error: ErrorContainer = {};
		let isValid = checkNonEmpty( data, "Email", error );
		if ( data.Email && !isEmailAddress( data.Email ) ) {
			isValid = false;
			error.Email = Strings.email_validation;
		}
		isValid = isValid && checkNonEmpty( data, "PassportNo", error );
		isValid = isValid && checkNonEmpty( data, "PassportIssueDate", error );
		isValid = isValid && checkNonEmpty( data, "PassportExpiryDate", error );
		if ( isValid ) {
			if ( data.PassportIssueDate!.asDate() > data.PassportExpiryDate!.asDate() ) {
				isValid = false;
				error.PassportIssueDate = Strings.start_date_should_be_after_end_date;
			}
		}
		isValid = isValid && checkNonEmpty( data, "ResidenceVisaNumber", error );
		isValid = isValid && checkNonEmpty( data, "VisaStartDate", error );
		isValid = isValid && checkNonEmpty( data, "VisaExpiryDate", error );
		if ( isValid ) {
			if ( data.VisaStartDate!.asDate() > data.VisaExpiryDate!.asDate() ) {
				isValid = false;
				error.VisaStartDate = Strings.start_date_should_be_after_end_date;
			}
		}
		this.setState( {
			errorContainer: error,
		} );
		return isValid;
	}

	private onContinue = () => {
		if ( this.isValid() ) {
			const { contract } = this.context;
			const data = this.getUpdatedData();
			if ( contract && contract.Tenants && contract.Tenants.length > 0 ) {
				const tenant = contract.Tenants[ 0 ];
				tenant.Email = data.Email;
				const personTenant = tenant.Person;
				_.merge( personTenant, data );
				updateTenantContractContext( { tenant: tenant } );
				if ( !this.context.isCommercial ) navigate( "co-occupants" );
				else navigate( "contract" );
			}
		}
	};

	private onBack = () => {
		this.props.history.go( -2 );
	};
}

export default TenantInfo;
